import React, { useState, useEffect } from 'react';
import {
  Download,
  Calendar,
  BarChart3,
  TrendingUp,
  Users,
  Target,
  DollarSign,
  PieChart,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell
} from 'recharts';
import { reportsAPI, dashboardAPI, leadsAPI, demosAPI, dealsAPI } from '../../services/api';
import { usePrivacy } from '../../contexts/PrivacyContext';

const COLORS = ['#0ea5e9', '#22c55e', '#f59e0b', '#8b5cf6', '#10b981'];

const ReportsPage = () => {
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  const [periodFilter, setPeriodFilter] = useState('this_month');
  const [loading, setLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [stats, setStats] = useState({
    totalLeads: 0,
    totalDemos: 0,
    sqds: 0,
    dealsWon: 0,
    totalRevenue: 0,
    conversionRate: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [trendRes, perfRes, funnelRes, leadsRes, demosRes, dealsRes] = await Promise.all([
          reportsAPI.monthlyTrend(6),
          reportsAPI.performance(),
          dashboardAPI.funnel(),
          leadsAPI.list({ page_size: 100 }),
          demosAPI.list({ page_size: 100 }),
          dealsAPI.list({ page_size: 100 })
        ]);
        
        // Use API response data - leads are in .leads, demos in .demos, deals directly
        const leadsData = leadsRes.data?.leads || leadsRes.data || [];
        const demosData = demosRes.data?.demos || demosRes.data || [];
        const dealsData = dealsRes.data?.deals || dealsRes.data || [];
        
        const totalLeads = leadsRes.data?.total || leadsData.length;
        const totalDemos = demosRes.data?.total || demosData.length;
        const sqds = demosData.filter(d => d.is_sqd).length;
        const wonDeals = (Array.isArray(dealsData) ? dealsData : []).filter(d => 
          ['WON_PAYMENT_RECEIVED', 'WON_PAYMENT_PENDING', 'LIVE_ACTIVE'].includes(d.deal_stage)
        );
        const totalRevenue = wonDeals.reduce((sum, d) => sum + (d.total_deal_value || d.deal_value_estimate || 0), 0);
        const conversionRate = totalLeads > 0 ? Math.round((wonDeals.length / totalLeads) * 100) : 0;
        
        setStats({
          totalLeads,
          totalDemos,
          sqds,
          dealsWon: wonDeals.length,
          totalRevenue,
          conversionRate
        });
        
        // Monthly trend data
        const trends = trendRes.data?.trends || [];
        setMonthlyData(trends.map(t => ({
          month: t.month?.split(' ')[0] || t.month,
          sqds: t.sqds || 0,
          conversions: t.deals || 0,
          leads: t.leads || 0
        })));

        // Team performance data
        const perfData = perfRes.data?.data || [];
        setPerformanceData(perfData.map(p => ({
          name: p.user_name || 'Unknown',
          sqds: p.sqd_count || 0,
          conversions: p.deals_won || 0,
          revenue: p.deal_value || 0
        })));

        // Lead funnel
        const funnel = funnelRes.data?.stages || [];
        setFunnelData(funnel.length > 0 ? funnel : [
          { stage: 'Leads', count: totalLeads },
          { stage: 'Contacted', count: Math.round(totalLeads * 0.7) },
          { stage: 'Demo Scheduled', count: totalDemos },
          { stage: 'SQD', count: sqds },
          { stage: 'Won', count: wonDeals.length }
        ]);
      } catch (error) {
        console.error('Failed to fetch reports data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [periodFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading reports...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100">Reports</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Analytics and performance insights</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="w-[130px] sm:w-[150px] bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-sm">
              <Calendar className="w-4 h-4 mr-1 sm:mr-2 text-slate-400" />
              <SelectValue placeholder="This Week" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="border-slate-200 dark:border-slate-700 hidden sm:flex">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* KPI Cards - Mobile Responsive Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-1">Total Leads</p>
                <p className="text-xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">{stats.totalLeads}</p>
                <p className="text-xs sm:text-sm text-slate-400 mt-1 hidden sm:block">All time</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-1">Demos / SQDs</p>
                <p className="text-xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">{stats.totalDemos} / {stats.sqds}</p>
                <p className="text-xs sm:text-sm text-emerald-600 mt-1 hidden sm:block">{stats.totalDemos > 0 ? Math.round((stats.sqds / stats.totalDemos) * 100) : 0}% qualified</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Target className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-1">Deals Won</p>
                <p className="text-xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">{stats.dealsWon}</p>
                <p className="text-xs sm:text-sm text-emerald-600 mt-1 hidden sm:block">{stats.conversionRate}% conv</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-1">Revenue</p>
                <p className="text-lg sm:text-3xl font-bold text-slate-800 dark:text-slate-100">{formatCurrency(stats.totalRevenue)}</p>
                <p className="text-xs sm:text-sm text-emerald-600 mt-1 hidden sm:block">Won deals</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Trend */}
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              Performance Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {monthlyData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyData}>
                    <defs>
                      <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="leads"
                      stroke="#0ea5e9"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorLeads)"
                      name="Leads"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  No trend data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Team Performance */}
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              Team Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {performanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={performanceData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                    <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={12} width={80} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Bar dataKey="sqds" fill="#334155" radius={[0, 4, 4, 0]} name="SQDs" />
                    <Bar dataKey="conversions" fill="#10b981" radius={[0, 4, 4, 0]} name="Conversions" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  No performance data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lead Funnel */}
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            Lead Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {funnelData.map((stage, index) => (
              <div key={stage.stage} className="text-center">
                <div 
                  className="h-16 sm:h-24 rounded-lg flex items-center justify-center text-white font-bold text-lg sm:text-xl"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                >
                  {stage.count}
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">{stage.stage}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
